import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Mark } from "./Nav";

const EMAIL = "alex.socialdiscovery@outlook.com";

const Section = ({ num, title, children }) => (
    <section className="mt-12">
        <h2 className="text-xl font-semibold tracking-tight text-[#F4F1EC] sm:text-2xl">
            {num}. {title}
        </h2>
        <div className="mt-4 space-y-4 text-base leading-relaxed text-[#C9C5C0]">{children}</div>
    </section>
);

const List = ({ items }) => (
    <ul className="list-disc space-y-2 pl-5 marker:text-[#C7B7FF]">
        {items.map((item, i) => (
            <li key={i}>{item}</li>
        ))}
    </ul>
);

const MailLink = () => (
    <a
        href={`mailto:${EMAIL}`}
        data-testid="privacy-email-link"
        className="text-[#C7B7FF] underline decoration-[#8A8580] underline-offset-4 transition-colors hover:decoration-[#C7B7FF]"
    >
        {EMAIL}
    </a>
);

const BackLink = ({ testid }) => (
    <Link
        to="/"
        data-testid={testid}
        className="nav-link inline-flex items-center gap-2 text-sm text-[#C9C5C0] hover:text-[#F4F1EC]"
    >
        <span aria-hidden="true">←</span> Back to Discovery Project
    </Link>
);

export const Privacy = () => {
    useEffect(() => {
        const meta = document.querySelector('meta[name="description"]');
        const prevTitle = document.title;
        const prevDesc = meta ? meta.getAttribute("content") : null;
        document.title = "Privacy Notice — Discovery Project";
        if (meta) {
            meta.setAttribute(
                "content",
                "Privacy information for the Discovery Project early research waitlist and product research."
            );
        }
        return () => {
            document.title = prevTitle;
            if (meta && prevDesc) meta.setAttribute("content", prevDesc);
        };
    }, []);

    return (
        <div data-testid="privacy-page" className="grain min-h-screen bg-[#0D0C0B] text-[#F4F1EC]">
            <header className="border-b border-[#262422] px-5 py-5 sm:px-8">
                <div className="mx-auto flex max-w-[820px] items-center justify-between">
                    <Link to="/" data-testid="privacy-brand-link" className="flex items-center gap-2.5">
                        <Mark size={18} />
                        <span className="text-sm font-semibold tracking-tight text-[#F4F1EC]">
                            DiscoveryProject
                        </span>
                    </Link>
                    <BackLink testid="privacy-back-top" />
                </div>
            </header>

            <main className="px-5 py-16 sm:px-8 sm:py-24">
                <article className="mx-auto max-w-[820px]">
                    <p className="font-mono2 text-xs uppercase tracking-[0.2em] text-[#C7B7FF]">
                        Last updated: 24 September 2026
                    </p>
                    <h1 className="mt-6 text-4xl font-bold tracking-tight text-[#F4F1EC] sm:text-5xl">
                        Privacy Notice
                    </h1>
                    <div className="mt-8 space-y-4 text-base leading-relaxed text-[#C9C5C0] sm:text-lg">
                        <p>Discovery Project is an early-stage social discovery research project.</p>
                        <p>
                            This Privacy Notice explains what personal information is collected
                            through this website and the early waitlist, why it is collected, and
                            what choices you have.
                        </p>
                    </div>

                    <Section num="1" title="Who is responsible for your data?">
                        <p>Data controller:</p>
                        <p className="text-[#F4F1EC]">Mihai Alexandru Cruceru</p>
                        <p>
                            Contact: <MailLink />
                        </p>
                        <p>
                            “Discovery Project” is currently a working project name and not
                            necessarily the final product or brand name.
                        </p>
                    </Section>

                    <Section num="2" title="What information do we collect?">
                        <p>If you join the early waitlist or participate in product research, we may collect:</p>
                        <List
                            items={[
                                "your email address",
                                "your country",
                                "your city or nearest city",
                                "what kinds of connections you might use the product for",
                                "your preferred device or platform",
                                "how you discovered the project",
                                "social or dating products you currently use",
                                "whether you are interested in prototype or beta testing",
                                "optional written answers about your interests, experiences, or what existing products fail to provide",
                            ]}
                        />
                        <p>
                            We do not currently require your real name, personal photographs, date
                            of birth, phone number, or social-media profiles to join the waitlist.
                        </p>
                        <p>
                            Please do not submit sensitive personal information that is not
                            necessary for the research.
                        </p>
                    </Section>

                    <Section num="3" title="Why do we collect this information?">
                        <p>We use this information to:</p>
                        <List
                            items={[
                                "manage the early waitlist",
                                "contact people who have asked to receive project updates",
                                "invite interested participants to prototypes, interviews, alpha or beta testing",
                                "understand demand for the product",
                                "understand geographic distribution and local user density",
                                "learn what kinds of connections people are looking for",
                                "understand which existing products people use and where those products may fall short",
                                "improve and validate the product concept before development",
                            ]}
                        />
                        <p>
                            The project is currently in an early research stage. Waitlist and
                            research data are intended to help determine what should eventually be
                            built.
                        </p>
                    </Section>

                    <Section num="4" title="Legal basis">
                        <p>
                            Where applicable under data-protection law, we process waitlist and
                            research information on the basis of your consent.
                        </p>
                        <p>
                            You may withdraw your consent at any time by contacting: <MailLink />
                        </p>
                        <p>
                            Withdrawing consent does not affect processing that took place before
                            the withdrawal.
                        </p>
                    </Section>

                    <Section num="5" title="Tally">
                        <p>
                            The waitlist and research questionnaire are currently provided through
                            Tally.
                        </p>
                        <p>
                            Tally processes form responses on behalf of the form creator.
                            Information submitted through the form may therefore be processed and
                            stored using Tally's infrastructure.
                        </p>
                        <p>For more information, please see Tally's own Privacy Notice.</p>
                    </Section>

                    <Section num="6" title="Website hosting and technical providers">
                        <p>
                            The website currently uses third-party infrastructure for hosting and
                            technical operation, including Emergent.
                        </p>
                        <p>
                            These providers may process limited technical information necessary to
                            deliver the website, maintain security, and operate their services, such
                            as network requests, IP addresses, device or browser information, or
                            server logs, according to their own terms and privacy policies.
                        </p>
                        <p>
                            We do not intend to use personal information from this website for
                            advertising profiles or to sell personal information to advertisers.
                        </p>
                        <p>
                            If analytics, advertising pixels, or additional tracking technologies
                            are introduced later, this Privacy Notice will be updated accordingly.
                        </p>
                    </Section>

                    <Section num="7" title="How long do we keep the data?">
                        <p>
                            Unless you request deletion earlier, waitlist and research information
                            may be retained for up to 24 months after your last meaningful
                            interaction with the project.
                        </p>
                        <p>Information may be deleted earlier when it is no longer useful for the research.</p>
                        <p>You may request deletion at any time.</p>
                    </Section>

                    <Section num="8" title="Who has access to the data?">
                        <p>
                            Access is limited to the project owner and service providers that are
                            necessary to operate the website, waitlist, and research workflow.
                        </p>
                        <p>Personal information will not be sold to advertisers.</p>
                        <p>
                            Aggregated or anonymised findings may be used to understand demand,
                            geography, product preferences, or research results without identifying
                            individual participants.
                        </p>
                    </Section>

                    <Section num="9" title="Your rights">
                        <p>
                            Depending on where you live, you may have rights relating to your
                            personal data, including the right to:
                        </p>
                        <List
                            items={[
                                "request access to your personal information",
                                "correct inaccurate information",
                                "request deletion",
                                "request a copy of information you provided",
                                "withdraw consent",
                                "object to or restrict certain processing where applicable",
                                "lodge a complaint with the competent data-protection authority",
                            ]}
                        />
                        <p>
                            To exercise these rights, contact: <MailLink />
                        </p>
                    </Section>

                    <Section num="10" title="Cookies and tracking">
                        <p>
                            The project does not intentionally use advertising cookies or
                            advertising pixels.
                        </p>
                        <p>
                            The website or its infrastructure providers may use technically
                            necessary mechanisms or collect limited technical information required
                            to operate and secure their services.
                        </p>
                        <p>
                            If the website later adopts analytics or other non-essential tracking
                            technologies, this section and any required consent mechanism will be
                            updated before those technologies are used.
                        </p>
                    </Section>

                    <Section num="11" title="Changes to this notice">
                        <p>This project is still evolving.</p>
                        <p>
                            This Privacy Notice may be updated when the research process, website
                            infrastructure, service providers, or data collection practices change.
                        </p>
                        <p>
                            The current version and its update date will always be available on this
                            page.
                        </p>
                    </Section>

                    <Section num="12" title="Contact">
                        <p>For privacy questions, requests, or withdrawal of consent:</p>
                        <p className="text-[#F4F1EC]">
                            Mihai Alexandru Cruceru
                            <br />
                            <MailLink />
                        </p>
                    </Section>

                    <div className="mt-16 border-t border-[#262422] pt-8">
                        <BackLink testid="privacy-back-bottom" />
                    </div>
                </article>
            </main>
        </div>
    );
};
